import { jsx, Fragment, jsxs } from "react/jsx-runtime";
import { RG_TYPE, GetAllBones } from "@project-rouge/rg-core";
import { getRgObjectsOfType, useFunction } from "@project-rouge/rg-three";
import { Line } from "@react-three/drei";
import { memo, useMemo } from "react";
import { MathUtils, DoubleSide } from "three";
import { Extrude } from "./index45.js";
import { getBuildingCenter } from "./index221.js";
import { useCursor } from "./index222.js";
import { useMemoState } from "./index38.js";
import { useDoubleRef } from "./index223.js";
import { getWingPoly } from "./index175.js";
const disableRaycast = () => {
};
const BuildingSelector = memo(({
  obj,
  onSelect,
  buildingName,
  topOffset,
  skipBuildingId
}) => {
  const allBuildings = useMemo(() => getRgObjectsOfType(obj, RG_TYPE.Building), [obj]);
  const buildings = !skipBuildingId ? allBuildings : allBuildings.filter((b) => b.uuid !== skipBuildingId);
  const [hover, setHover] = useMemoState(void 0, [skipBuildingId]);
  const hoverRef = useDoubleRef(hover);
  const onHover = useFunction((building) => {
    if (hoverRef.current === building)
      return;
    hoverRef.current = building;
    setHover(building);
  });
  const onHoverOut = useFunction((building) => {
    if (hoverRef.current !== building)
      return;
    hoverRef.current = void 0;
    setHover(void 0);
  });
  return /* @__PURE__ */ jsx(Fragment, { children: buildings.map((b) => /* @__PURE__ */ jsx(
    BuildingButton,
    {
      building: b,
      hover: hover === b,
      onPointerEnter: () => onHover(b),
      onPointerOut: () => onHoverOut(b),
      onClick: () => onSelect(b.uuid),
      buildingName,
      topOffset
    },
    b.uuid
  )) });
});
BuildingSelector.displayName = "BuildingSelector";
const BuildingButton = ({ building, onClick, buildingName, hover, onPointerEnter, onPointerOut, topOffset }) => {
  const bones = useMemo(() => GetAllBones(building.data.anatomy), [building]);
  const center = useMemo(() => getBuildingCenter(building), [building]);
  useCursor(hover);
  const _onPointerEnter = useFunction((e) => {
    e.stopPropagation();
    onPointerEnter();
  });
  const _onClick = useFunction((e) => {
    e.stopPropagation();
    onClick();
  });
  return /* @__PURE__ */ jsxs(
    "object3D",
    {
      onPointerMove: _onPointerEnter,
      onPointerOut,
      onClick: _onClick,
      children: [
        hover && /* @__PURE__ */ jsx("object3D", { position: center, children: buildingName({ building }) }),
        bones.map((bone) => /* @__PURE__ */ jsx(
          WingBoundary,
          {
            bone,
            building,
            hover,
            topOffset
          },
          bone.id
        ))
      ]
    }
  );
};
const WingBoundary = ({ building, bone, hover, topOffset }) => {
  const { topBoundary, bottomBoundary, top } = useMemo(() => {
    const topBoundary2 = getWingPoly({ building, bone, level: bone.floors - 1 });
    const bottomBoundary2 = building.data.levels.find((lvl) => lvl.levelIndex === 0).geaPolygon;
    const top2 = building.data.levels.find((lvl) => lvl.levelIndex === bone.floors).floorWorldBottom;
    return { topBoundary: topBoundary2, bottomBoundary: bottomBoundary2, top: top2 };
  }, [building, bone]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      Extrude,
      {
        visible: false,
        rotation: [90 * MathUtils.DEG2RAD, 0, 0],
        points: topBoundary,
        "material-side": DoubleSide,
        "material-transparent": true,
        "material-color": "#EBECED",
        "material-opacity": 0.3,
        "material-depthTest": false,
        "material-depthWrite": false,
        renderOrder: 10,
        extrude: {
          steps: 1,
          depth: -top - topOffset,
          bevelEnabled: false
        }
      }
    ),
    /* @__PURE__ */ jsx(
      Line,
      {
        position: [0, top + topOffset, 0],
        rotation: [90 * MathUtils.DEG2RAD, 0, 0],
        points: topBoundary,
        color: "#3B3B5F",
        transparent: true,
        visible: hover,
        raycast: disableRaycast
      }
    ),
    /* @__PURE__ */ jsx(
      Line,
      {
        points: bottomBoundary,
        color: "#3B3B5F",
        transparent: true,
        visible: hover,
        lineWidth: 2,
        raycast: disableRaycast
      }
    )
  ] });
};
export {
  BuildingSelector
};
//# sourceMappingURL=index170.js.map
